// packages/api/src/public/magic-link-errors.ts
//
// Link morto do magic-link: o backend responde `401 MAGIC_LINK_TOKEN_INVALID`
// quando o token-mãe de `/p/:token` não vale mais. É estado TERMINAL — nenhuma
// tentativa do associado destrava, só um link novo emitido pelo consultor.
//
// Fonte única do código, do evento de janela e dos predicados usados por (a) o
// interceptor do `publicApiClient`, (b) os pollings da jornada, que cessam, e
// (c) o `MagicLinkGate`, que troca a jornada pelo estado terminal.

/** Código do backend para token-mãe inválido — HTTP 401. */
export const MAGIC_LINK_TOKEN_INVALID = 'MAGIC_LINK_TOKEN_INVALID';

/**
 * Evento de janela emitido pelo interceptor do `publicApiClient` quando uma
 * chamada `/p/:token(/*)` recebe `401 MAGIC_LINK_TOKEN_INVALID`. `detail.token`
 * carrega o token-mãe da rota.
 */
export const MAGIC_LINK_TOKEN_INVALID_EVENT = 'alamo:magic-link-token-invalid';

/**
 * TRUE apenas para a recusa terminal do token-mãe (401 + code). Erro de rede
 * (sem `response`) e qualquer outro código respondem FALSE — a jornada segue
 * tratando-os como falha transitória, com retentativa e mensagem de conexão.
 */
export function isMagicLinkTokenInvalidError(error: unknown): boolean {
  if (error == null || typeof error !== 'object') return false;
  const response = (error as { response?: unknown }).response;
  if (response == null || typeof response !== 'object') return false;
  const { status, data } = response as { status?: unknown; data?: unknown };
  if (status !== 401) return false;
  if (data == null || typeof data !== 'object') return false;
  return (data as { code?: unknown }).code === MAGIC_LINK_TOKEN_INVALID;
}

// As duas assinaturas abaixo declaram o erro como `Error`, o `TError` default do
// TanStack Query. Aceitar `unknown` alarga a inferência de `TError` em cada
// `useQuery` que as usa, e o resultado deixa de ser atribuível a
// `UseQueryResult<T>` nos componentes que consomem o hook.

/**
 * `retry` das queries da jornada pública: link morto não retenta; os demais
 * erros mantêm a única retentativa do default do QueryClient (`retry: 1`).
 */
export function magicLinkQueryRetry(failureCount: number, error: Error): boolean {
  if (isMagicLinkTokenInvalidError(error)) return false;
  return failureCount < 1;
}

/**
 * `refetchOnWindowFocus` das queries da jornada pública: voltar para a aba não
 * pode ressuscitar a chamada de um link morto.
 */
export function magicLinkRefetchOnWindowFocus(query: { state: { error: Error | null } }): boolean {
  return !isMagicLinkTokenInvalidError(query.state.error);
}
